import { useMemo, useState } from 'react';
import { SearchValue } from '../components/SearchableHeader';
import { SortValue } from '../components/SortableHeader';
import ISearcher from '../ISearcher';
import ISorter from '../ISorter';
import { searchItems } from './useSearch';
import { sortItems } from './useSorting';

/**
 * A hook for searching and sorting items
 * @param items The items to search and sort through
 * @param initialSearch The initial search value
 * @param initialSort
 */
export const useSortedSearch = <T, TSortProps, TSearchProps>(
  items: T[],
  sorter: ISorter<T, TSortProps>,
  searcher: ISearcher<T, TSearchProps>,
  initialSort?: SortValue<TSortProps>,
  initialSearch?: SearchValue<TSearchProps>,
): [
  T[],
  SortValue<TSortProps> | undefined,
  SearchValue<TSearchProps> | undefined,
  (sort?: SortValue<TSortProps> | undefined) => void,
  (search?: SearchValue<TSearchProps> | undefined) => void
] => {
  const [sort, setSort] = useState<SortValue<TSortProps> | undefined>(initialSort);
  const [search, setSearch] = useState<SearchValue<TSearchProps> | undefined>(initialSearch);

  // Get the resulting items, memorized so they only change when items, search or sort change
  // eslint-disable-next-line max-len
  const resultItems = useMemo(() => searchItems(sortItems(items, sorter, sort), searcher, search), [items, sorter, searcher, search, sort]);

  return [resultItems, sort, search, setSort, setSearch];
};

export default useSortedSearch;
